import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/components/ui/use-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@/pages/auth/auth.context";
import { Loading } from "@/components/ui/loading";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { UserPublicInfoDto } from "@/pages/admin/events/types";
import { Position } from "@/services/positionService";
import { StatusPill } from "@/components/brand/StatusPill";

interface EventSettings {
  id: string;
  cancellationDaysBeforeEvent: number;
}

interface AutoAssignmentConfigDTO {
  id?: string;
  enabled: boolean;
  userId: string;
  userFirstName?: string;
  userLastName?: string;
  positionIds: string[];
  positions?: PositionDTO[];
}

interface PositionDTO {
  id: string;
  tableName: string;
  description?: string;
}

interface UserDto {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phoneNumber?: string;
}

export default function ManageSettings() {
  const [cancellationWindow, setCancellationWindow] = useState<number>(3);
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [selectedPositions, setSelectedPositions] = useState<string[]>([]);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  // Fetch current settings
  const { isLoading, data: settings } = useQuery({
    queryKey: ["event-settings"],
    queryFn: async () => {
      const response = await axiosInstance.get<EventSettings>("event-settings");
      setCancellationWindow(response.data.cancellationDaysBeforeEvent);
      return response.data;
    },
    meta: {
      onError: () => {
        toast({
          title: "Error",
          description: "Failed to load settings",
          variant: "destructive",
        });
      },
    },
  });
  const { data: users } = useQuery({
    queryKey: ["users-for-auto-assignment"],
    queryFn: async (): Promise<UserPublicInfoDto[]> => {
      const response = await axiosInstance.get("user?status=ACTIVE");
      return response.data.map((user: UserDto) => ({
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phoneNumber: user.phoneNumber,
      }));
    },
  });

  const { data: positions } = useQuery({
    queryKey: ["positions-for-auto-assignment"],
    queryFn: async (): Promise<Position[]> => {
      const response = await axiosInstance.get("position");
      return response.data;
    },
  });

  // Fetch existing auto-assignment configurations
  const { data: autoAssignConfigs, isLoading: autoAssignLoading } = useQuery({
    queryKey: ["auto-assignment-configs"],
    queryFn: async (): Promise<AutoAssignmentConfigDTO[]> => {
      const response = await axiosInstance.get("auto-assignment");
      return response.data;
    },
  });

  const { mutate: updateSettings, isPending } = useMutation({
    mutationFn: async () => {
      const response = await axiosInstance.put("event-settings", null, {
        params: {
          cancellationDaysBeforeEvent: Number(cancellationWindow),
        },
      });
      return response.data;
    },
    onSuccess: () => {
      toast({
        title: "Settings Saved",
        description: "Your changes have been saved successfully.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to save settings",
        variant: "destructive",
      });
    },
  });

  const { mutate: createAutoAssignConfig, isPending: isCreating } = useMutation(
    {
      mutationFn: async (config: AutoAssignmentConfigDTO) => {
        const response = await axiosInstance.post("auto-assignment", config);
        return response.data;
      },
      onSuccess: () => {
        toast({
          title: "Auto-Assignment Created",
          description:
            "Auto-assignment configuration has been created successfully.",
        });
        queryClient.invalidateQueries({
          queryKey: ["auto-assignment-configs"],
        });
        setSelectedUserId("");
        setSelectedPositions([]);
      },
      onError: () => {
        toast({
          title: "Error",
          description: "Failed to create auto-assignment configuration",
          variant: "destructive",
        });
      },
    },
  );

  // Update auto-assignment configuration mutation
  const { mutate: updateAutoAssignConfig } = useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      const response = await axiosInstance.put(
        `auto-assignment/${id}/enabled?enabled=${enabled}`,
      );
      return response.data;
    },
    onSuccess: () => {
      toast({
        title: "Auto-Assignment Updated",
        description:
          "Auto-assignment configuration has been updated successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["auto-assignment-configs"] });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update auto-assignment configuration",
        variant: "destructive",
      });
    },
  });

  const { mutate: deleteAutoAssignConfig } = useMutation({
    mutationFn: async (id: string) => {
      await axiosInstance.delete(`auto-assignment/${id}`);
    },
    onSuccess: () => {
      toast({
        title: "Auto-Assignment Deleted",
        description:
          "Auto-assignment configuration has been deleted successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["auto-assignment-configs"] });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete auto-assignment configuration",
        variant: "destructive",
      });
    },
  });

  const handleSave = () => {
    updateSettings();
  };

  const handleCreateAutoAssignment = () => {
    if (!selectedUserId || selectedPositions.length === 0) {
      toast({
        title: "Error",
        description: "Please select a user and at least one position",
        variant: "destructive",
      });
      return;
    }

    createAutoAssignConfig({
      enabled: false,
      userId: selectedUserId,
      positionIds: selectedPositions,
    });
  };

  const handlePositionToggle = (positionId: string) => {
    setSelectedPositions((prev) =>
      prev.includes(positionId)
        ? prev.filter((id) => id !== positionId)
        : [...prev, positionId],
    );
  };

  if (isLoading || autoAssignLoading) {
    return (
      <div className="container mx-auto p-6">
        <Loading text="Loading settings..." />
      </div>
    );
  }
  return (
    <div className="container mx-auto p-6 space-y-6">
      <h1 className="text-3xl font-bold mb-8">System Settings</h1>

      {/* Event Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle>Event Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="cancellationWindow">
              Event Cancellation Window (days)
            </Label>
            <div className="flex gap-4 items-start">
              <Input
                id="cancellationWindow"
                type="number"
                min="0"
                value={cancellationWindow}
                onChange={(e) => setCancellationWindow(Number(e.target.value))}
                className="max-w-[200px]"
              />
              <p className="text-sm text-muted-foreground">
                Number of days before an event that users are allowed to cancel
                their registration.
              </p>
            </div>
          </div>

          <Button onClick={handleSave} disabled={isPending} className="mt-6">
            {isPending ? "Saving..." : "Save Changes"}
          </Button>
        </CardContent>
      </Card>

      {/* Auto-Assignment Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle>Auto-Assignment Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Create New Auto-Assignment */}
          <div className="space-y-4 border-b pb-6">
            <h3 className="text-lg font-semibold">
              Create New Auto-Assignment
            </h3>

            <div className="space-y-2">
              <Label htmlFor="user-select">Select User</Label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger className="max-w-[300px]">
                  <SelectValue placeholder="Choose a user..." />
                </SelectTrigger>
                <SelectContent>
                  {users &&
                    users
                      .sort((a, b) =>
                        `${a.firstName} ${a.lastName}`.localeCompare(
                          `${b.firstName} ${b.lastName}`,
                        ),
                      )
                      .map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.firstName} {user.lastName}
                          {user.email && (
                            <span className="text-muted-foreground ml-2">
                              ({user.email})
                            </span>
                          )}
                        </SelectItem>
                      ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Select Positions for Auto-Assignment</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-40 overflow-y-auto border rounded-md p-3">
                {positions &&
                  positions
                    .sort((a, b) => a.tableName.localeCompare(b.tableName))
                    .map((position) => (
                      <div
                        key={position.id}
                        className="flex items-center space-x-2"
                      >
                        <Checkbox
                          id={`position-${position.id}`}
                          checked={selectedPositions.includes(position.id)}
                          onCheckedChange={() =>
                            handlePositionToggle(position.id)
                          }
                        />
                        <Label
                          htmlFor={`position-${position.id}`}
                          className="text-sm font-normal cursor-pointer"
                        >
                          {position.tableName}
                        </Label>
                      </div>
                    ))}
              </div>
            </div>

            <Button
              onClick={handleCreateAutoAssignment}
              disabled={
                isCreating || !selectedUserId || selectedPositions.length === 0
              }
              className="w-full sm:w-auto"
            >
              {isCreating ? "Creating..." : "Create Auto-Assignment"}
            </Button>
          </div>

          {/* Existing Auto-Assignments */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Existing Auto-Assignments</h3>
            {autoAssignConfigs && autoAssignConfigs.length > 0 ? (
              <div className="space-y-3">
                {[...autoAssignConfigs]
                  .sort((a, b) =>
                    (a.userFirstName ?? "").localeCompare(
                      b.userFirstName ?? "",
                    ),
                  )
                  .map((config) => (
                    <Card key={config.id}>
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div className="space-y-1">
                            <div className="font-medium">
                              {config.userFirstName} {config.userLastName}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              Positions:{" "}
                              {config.positions
                                ?.map((p) => p.tableName)
                                .join(", ") ||
                                `${config.positionIds.length} position(s) selected`}
                            </div>
                            <div className="flex items-center gap-2">
                              {/* Muted, not red: a switched-off rule is
                                  inactive, not an error. */}
                              <StatusPill
                                tone={config.enabled ? "success" : "muted"}
                              >
                                {config.enabled ? "Enabled" : "Disabled"}
                              </StatusPill>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={config.enabled}
                              onCheckedChange={(enabled) =>
                                config.id &&
                                updateAutoAssignConfig({
                                  id: config.id,
                                  enabled,
                                })
                              }
                            />
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() =>
                                config.id && deleteAutoAssignConfig(config.id)
                              }
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
              </div>
            ) : (
              <p className="text-muted-foreground">
                No auto-assignment configurations found.
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
